/**
 * Web Component for the Shopify Search & Discovery filtering and sorting functionality.
 * 
 * This web component is responsible for handling user inputs across the filter/sort forms, including:
 *     - Listening for input changes
 *     - Querying the Shopify API using the selected filters
 *     - Displaying the results of the query to the user
 */

class FacetFiltersForm extends HTMLElement 
{
    // IDs
    static activeFiltersContainer = 'ActiveFiltersContainer';
    static countContainer = 'ProductCountContainer';
    static filtersContainer = 'FiltersContainer';
    static form = 'FiltersForm';
    static gridContainer = 'ProductGridContainer';
    static grid = 'product-loop';
    static mobileToggle = 'filter_form_toggle_button';
    static toggleMore = 'filter_form_toggle_more';

    // Classes
    static filterCheckbox = 'filter-form__checkbox';
    static filterColourSwatch = 'filter-form__colour-swatch';
    static filterGroup = 'filter-form__group';
    static filterGroupHidden = 'filter-form__group-hidden';
    static filterItemDisabled = 'filter-form__group-display-list-item-disabled';
    static filterItemTitle = 'filter-form__group-display-list-item-title';
    static filterList = 'filter-form__list';
    static filterRange = 'filter-form__range';
    static productCard = 'item';
    static sortSelect = 'filter-form__select';

    /**
     * Constructor for the FacetFiltersForm class.
     */
    constructor () 
    {
        super();
        this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

        // Delay the input changed event by 0.5s in-case the user is typing
        this.debouncedOnSubmit = debounce((event) => {
            // Set the initial params when the form is submitted
            var initialParams = FacetFiltersForm.searchParamsInitial;
            if (!initialParams || typeof initialParams === 'undefined')
                FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);

            // Set the starting previous params when the form is submitted
            var previousParams = FacetFiltersForm.searchParamsPrev;
            if (!previousParams || typeof previousParams === 'undefined')
                FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);

            this.onSubmitHandler(event);
        }, 500);

        const facetForm = this.querySelector('form');
        facetForm.addEventListener('input', (event) => {
            // Only handle input events for filter controls, not the instock toggle
            if (event.target.closest('.show-instock-only')) {
                // Let the toggle handler manage this
                return;
            }
            this.debouncedOnSubmit(event);
        });

        document.addEventListener('click', function (e) {
            e = e || window.event;
            var target = e.target || e.srcElement;

            var openGroups = document.querySelectorAll(`.${FacetFiltersForm.filterGroup}[open]`);
            if (openGroups && typeof openGroups !== 'undefined') {
                openGroups.forEach(group => {
                    if (!(group == target || group.contains(target)))
                        group.removeAttribute('open');
                });
            }
        });

        FacetFiltersForm.addAfterEffects();
    }

    static setupListeners ()
    {
        // Setup the listener for the mobile filter toggle button
        let mobileToggleButton = document.getElementById(FacetFiltersForm.mobileToggle);
        if (mobileToggleButton && typeof mobileToggleButton !== 'undefined') {
            mobileToggleButton.addEventListener('click', function () {
                mobileToggleButton.classList.toggle('filter-form__button-toggled');

                var filterToggles = document.getElementById(FacetFiltersForm.filtersContainer);
                if (filterToggles && filterToggles.classList.length > 0)
                    filterToggles.classList.toggle(`${filterToggles.classList[0]}-visible`);
            });
        }

        // Handle opening/closing details tags when a new one is toggled open
        let filterToggles = document.querySelectorAll(`.${FacetFiltersForm.filterGroup}`);
        if (filterToggles && typeof filterToggles !== 'undefined') {
            filterToggles.forEach(group => {
                // Add a click listener to each details tag
                group.addEventListener('click', function () {
                    // Get the existing open details tags and close any which aren't the desired one
                    var openGroups = document.querySelectorAll(`.${FacetFiltersForm.filterGroup}[open]`);
                    if (openGroups && typeof openGroups !== 'undefined') {
                        openGroups.forEach(openGroup => {
                            if (openGroup !== group)
                                openGroup.removeAttribute('open');
                        });
                    }
                });
            });
        }

        // Handle showing/hiding of hidden filters
        let moreFiltersToggle = document.getElementById(FacetFiltersForm.toggleMore);
        if (moreFiltersToggle && typeof moreFiltersToggle !== 'undefined') {
            moreFiltersToggle.addEventListener('click', function () {
                moreFiltersToggle.parentElement.classList.add('filter-form__toggle-more-button-hidden');

                var filterList = document.querySelector(`.${FacetFiltersForm.filterList}`);
                if (filterList && typeof filterList !== 'undefined')
                    filterList.classList.add(`${FacetFiltersForm.filterList}-extended`);
            });
        }
    }

    /**
     * Handles the user clicking the browser back-button.
     * 
     * The filters are applied via a fetch request, so any manual back-button presses need to
     * be intercepted and handled to simulate going back.
     */
    static setHistoryChangeListeners ()
    {
        // If the history changes, grab the search params and re-render the previous page if they've changed
        window.addEventListener('popstate', (event) => {
            let searchParams = event.state && event.state.searchParams ? event.state.searchParams : null;

            // searchParams is set to initial to remove the first filter that has been set. This can only
            // be a possible case when there are no more pages to unload as a filter application always resets to one page.
            if (searchParams == null) {
                let markers = document.querySelectorAll('[data-page-marker]');
                if (markers && typeof markers !== 'undefined' && markers.length == 1)
                    searchParams = FacetFiltersForm.searchParamsInitial;
            }

            if (!searchParams || typeof searchParams === 'undefined' || searchParams === FacetFiltersForm.searchParamsPrev)
                return;

            FacetFiltersForm.renderPage(searchParams, null, false);
        });
    }

    /**
     * Adds after effects to the filters, namely colour swatches and transitions.
     */
    static addAfterEffects ()
    {
        const filtersContainer = document.getElementById(FacetFiltersForm.filtersContainer);

        // Add transitions to overflowing filter items, i.e. items that overflow their text container.
        const allFilterGroups = filtersContainer?.querySelectorAll(`.${FacetFiltersForm.filterGroup}`);

        if (allFilterGroups && typeof allFilterGroups !== 'undefined' && allFilterGroups.length > 0) {
            allFilterGroups.forEach(filterGroup => {
                const options = filterGroup.querySelectorAll(`.${FacetFiltersForm.filterItemTitle}`);
                options.forEach(option => {
                    option.addEventListener('mouseover', function () {
                        if (!option.closest(`.${FacetFiltersForm.filterItemDisabled}`)) {
                            let parentElement = option.parentElement;
                            let bufferRight = parentElement.children[parentElement.children.length - 1];
        
                            if ((option.clientWidth + bufferRight.clientWidth) > parentElement.clientWidth) {
                                let translation = `translateX(-${ (option.clientWidth + bufferRight.clientWidth) - parentElement.clientWidth }px)`;
        
                                option.style.msTransform = translation;
                                option.style.transform = translation;
                                option.style.webkitTransform = translation;
                            }
                        }
                    });
    
                    option.addEventListener('mouseout', function () {
                        option.style.msTransform = "translateX(0px)";
                        option.style.transform = "translateX(0px)";
                        option.style.webkittransform = "translateX(0px)";
                    });
                });
            });
    
            // Add colour swatches to the colour filter
            const colourFilterGroups = filtersContainer.querySelectorAll(`.${FacetFiltersForm.filterGroup}[data-filter-type='colour']`);
            colourFilterGroups.forEach(filterGroup => {
                const colours = filterGroup.querySelectorAll(`[data-colour-name]`);
                colours.forEach(colour => {
                    var colourCode = window.facets.colourFacets[colour.getAttribute('data-colour-name')];
                    var colourSwatch = colour.querySelector(`.${FacetFiltersForm.filterColourSwatch}`);
    
                    if (colourCode && typeof colourCode !== 'undefined' && colourCode.colour && typeof colourCode.colour !== 'undefined') {
                        colourSwatch.style.backgroundColor = colourCode.colour;
                        colourSwatch.classList.remove(`${FacetFiltersForm.filterColourSwatch}-hidden`);
                    }
                });
            });
        }
    }

    /**
     * Renders the individual elements that make up the filter form and product grid.
     * 
     * @param {*} html The HTML to load in.
     * @param {*} event The event that fired the render.
     */
    static renderData (html)
    {
        // Render the filters, product grid and product count
        FacetFiltersForm.renderFilters(html);
        FacetFiltersForm.renderActiveFacets(html);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
    }


    /**
     * Renders the page/section from the existing cache.
     * 
     * @param {*} filterDataUrl The URL for the cache to find.
     */
    static renderSectionFromCache (filterDataUrl)
    {
        // Grab the HTML from the stored URL/HTML pairs in the cache.
        const html = FacetFiltersForm.filterData.find(filterDataUrl).html;

        FacetFiltersForm.renderData(html);
    }

    /**
     * Renders the page/section from a fresh fetch of a URL.
     * 
     * @param {*} url The URL to fetch.
     */
    static renderSectionFromFetch (url) 
    {
        fetch(url)
            .then(response => response.text())
            .then((responseText) => {
                const html = responseText;

                // Cache the filterData so it can be accessed later if necessary.
                FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];

                FacetFiltersForm.renderData(html);
            });
    }

    /**
     * Renders the page using the provided search parameters.
     * 
     * @param {*} searchParams The search parameters grabbed from the form.
     * @param {*} event The event that fired the page render.
     * @param {*} updateURLHash Whether or not to update the URL hash/query string.
     */
    static renderPage (searchParams, event, updateURLHash = true) 
    {
        FacetFiltersForm.searchParamsPrev = searchParams;

        // Add a loading class to the products grid to show it's changing
        const container = document.getElementById(FacetFiltersForm.gridContainer);
        if (container && typeof container !== 'undefined' && container.children.length > 0)
            container.children[0].classList.add('loading');

        if (event && typeof event !== 'undefined' && event.srcElement && typeof event.srcElement !== 'undefined')
        {
            // Add a loading class to the <details> element to show it's changing
            const filterElement = event.srcElement.closest('.filter-form__group-display');
            if (filterElement && typeof filterElement !== 'undefined' && filterElement.classList.length > 0)
                filterElement.classList.add('loading');
        }

        // Guard against the element not existing if all products in the collection are hidden
        if (FacetFiltersForm.grid) {
            const gridEl = document.getElementById(`${FacetFiltersForm.grid}`);

            if (gridEl) {
                // Grab the section ID and generate the URL to filter by
                var sectionId = gridEl.dataset?.id;

                if (sectionId) {
                    const url = `${window.location.pathname}?section_id=${sectionId}&${searchParams}`;
                    const filterDataUrl = element => element.url === url;

                    // Check whether filterDataUrl exists in the filterData array. If it does, the user has likely 
                    // gone back in the history, so we load from the cache. Otherwise, we load the URL from scratch.
                    FacetFiltersForm.filterData.some(filterDataUrl) ?
                        FacetFiltersForm.renderSectionFromCache(filterDataUrl) :
                        FacetFiltersForm.renderSectionFromFetch(url);

                    if (updateURLHash)
                        history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
                }
            }
        }
    }

    /**
     * Renders the filters after the input element is changed.
     * 
     * @param {*} html The HTML to be used to replace the existing filters.
     */
    static renderFilters (html)
    {
        const filtersContainer = document.getElementById(FacetFiltersForm.filtersContainer);
        const filterGroups = filtersContainer?.querySelectorAll(`.${FacetFiltersForm.filterGroup}`);

        // Grab the returned filters HTML
        var parser = new DOMParser;
        var parsedHTML = parser.parseFromString(html, 'text/html');

        if (filterGroups && typeof filterGroups !== 'undefined' && filterGroups.length > 0)
        {
            filterGroups.forEach(filterGroup => {
                if (parsedHTML.querySelector(`.${FacetFiltersForm.filterGroup}[data-index="${filterGroup.dataset.index}"]`)) {
                    filterGroup.innerHTML = parsedHTML.querySelector(`.${FacetFiltersForm.filterGroup}[data-index="${filterGroup.dataset.index}"]`).innerHTML;
                    filterGroup.classList.remove(FacetFiltersForm.filterGroupHidden);
                } else {
                    filterGroup.classList.add(FacetFiltersForm.filterGroupHidden);
                }
            });

            FacetFiltersForm.addAfterEffects();
        }

        // Update the sort by dropdown to match the returned HTML
        const sortBy = document.querySelector(`.${FacetFiltersForm.sortSelect}`);
        if (sortBy && typeof sortBy !== 'undefined')
            sortBy.innerHTML = parsedHTML.querySelector(`.${FacetFiltersForm.sortSelect}`).innerHTML;
    }

    /**
     * Renders the active facets after the input element is changed.
     * 
     * @param {*} html 
     */
    static renderActiveFacets (html)
    {
        // Grab the returned facets HTML
        var parser = new DOMParser;
        var parsedElement = parser.parseFromString(html, 'text/html').getElementById(FacetFiltersForm.activeFiltersContainer);
        var activeFiltersContainer = document.getElementById(FacetFiltersForm.activeFiltersContainer);

        // Remove the active filters div if the last active filter has been detached
        if (parsedElement == null || typeof parsedElement == 'undefined')
        {
            if (activeFiltersContainer && typeof activeFiltersContainer !== 'undefined')
                activeFiltersContainer.remove();
        } else {
            const activeContainer = document.getElementById(FacetFiltersForm.activeFiltersContainer);
            if (activeContainer && typeof activeContainer !== 'undefined')
            {
                // Set the active facets content to the returned html
                if (activeFiltersContainer && typeof activeFiltersContainer !== 'undefined')
                    activeFiltersContainer.innerHTML = parsedElement.innerHTML;
            } else {
                // The active values container is not in the DOM (as this is the first active value), so create it
                let newContainer = document.createElement('div');
                newContainer.setAttribute('id', FacetFiltersForm.activeFiltersContainer);
                newContainer.innerHTML = parsedElement.innerHTML;

                var filtersContainer = document.getElementById(FacetFiltersForm.filtersContainer);
                filtersContainer.insertAdjacentElement("afterend", newContainer);
            }
        }
    }

    /**
     * Renders the product grid container after the updated DOM is fetched.
     * 
     * @param {*} html The HTML to be used to replace the existing grid container content.
     */
    static renderProductGridContainer (html)
    {
        // Grab the returned products HTML
        var parser = new DOMParser;
        var parsedHTML = parser.parseFromString(html, 'text/html').getElementById(FacetFiltersForm.gridContainer).innerHTML;

        // Set the product grid contents to the returned html
        var productGridContainer = document.getElementById(FacetFiltersForm.gridContainer);
        productGridContainer.innerHTML = parsedHTML;

        // Re-apply hidden products logic to newly injected product grid HTML
        if (window.HiddenProducts && typeof HiddenProducts.hide === 'function')
            HiddenProducts.hide();

        // Check if only one product is returned - if so, redirect to product.
        var productCards = productGridContainer.querySelectorAll(`#${FacetFiltersForm.grid} > .${FacetFiltersForm.productCard}`);
        if (productCards && typeof productCards !== 'undefined' && productCards.length == 1) {
            var href = productCards[0].querySelector('a').getAttribute('href');
            window.location.href = href;
        }

        // Re-attach event listeners set in LoadMore when the product grid is changed/replaced
        (async () => {
            console.log("Waiting for LoadMore to be available");
            let tries = 0;
            
            // Make ten attempts at finding the LoadMore class
            while (!LoadMore && tries < 10) {
              await new Promise(resolve => setTimeout(resolve, 100));
              tries++;
            }
            
            // Reset the product grid event listeners for the LoadMore functionality
            if (LoadMore)
              LoadMore.setProductGridListeners();
        })();          
    }

    /**
     * Renders the product count container after the updated DOM is fetched.
     * 
     * @param {*} html The HTML to be used to replace the existing count container.
     */
    static renderProductCount (html) 
    {
        // Grab the returned products count section HTML
        var parser = new DOMParser;
        var parsedHTML = parser.parseFromString(html, 'text/html').getElementById(FacetFiltersForm.countContainer).innerHTML;

        document.getElementById(FacetFiltersForm.countContainer).innerHTML = parsedHTML;
    }

    /**
     * Handles the form submission event fired by a form element receiving an input.
     * 
     * @param {*} event The event fired by an input change.
     */
    onSubmitHandler (event) 
    {
        event.preventDefault();

        // If the submission event was fired by a valid form element, generate the query and return the results.
        var acceptedInputs = [FacetFiltersForm.filterCheckbox, FacetFiltersForm.filterRange, FacetFiltersForm.sortSelect];
        if (acceptedInputs.some(className => event.srcElement.classList.contains(className)))
        {
            const formData = new FormData(document.getElementById(FacetFiltersForm.form));
            const searchParams = new URLSearchParams(formData).toString();

            FacetFiltersForm.renderPage(searchParams, event);
        }
    }

    /**
     * Handles the click event fired by clicking an active filter.
     * 
     * @param {*} event  The event fired by a click.
     */
    onActiveFilterClick (event) 
    {
        event.preventDefault();

        const url = event.currentTarget.href.indexOf('?') == -1 ? '' : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
        FacetFiltersForm.renderPage(url);
    }
}

customElements.define('facet-filters-form', FacetFiltersForm);

FacetFiltersForm.filterData = [];
FacetFiltersForm.setupListeners();
FacetFiltersForm.setHistoryChangeListeners();

class PriceSlider extends HTMLElement
{
    constructor () 
    {
        super();

        this.slider = this.querySelector('.filter-form__group-display-price-slider .slider');

        if (this.slider && typeof this.slider !== 'undefined')
        {
            this.sliderHandleLeft = this.slider.querySelector('.slider-handle-left');
            this.sliderHandleRight = this.slider.querySelector('.slider-handle-right');

            if (this.sliderHandleLeft && typeof this.sliderHandleLeft !== 'undefined' 
                && this.sliderHandleRight && typeof this.sliderHandleRight !== 'undefined') 
            {
                this.handleEvent = this.handleEvent.bind(this);

                // Attach the listeners to the slider components
                this.sliderHandleLeft.addEventListener('mouseover', this.handleEvent);
                this.sliderHandleLeft.addEventListener('mouseleave', this.handleEvent);
                this.sliderHandleLeft.addEventListener('mousedown', this.handleEvent);
                this.sliderHandleLeft.addEventListener('touchstart', this.handleEvent);
                this.sliderHandleRight.addEventListener('mouseover', this.handleEvent);
                this.sliderHandleRight.addEventListener('mouseleave', this.handleEvent);
                this.sliderHandleRight.addEventListener('mousedown', this.handleEvent);
                this.sliderHandleRight.addEventListener('touchstart', this.handleEvent);

                // Attach the listeners to the document
                document.addEventListener('mousemove', this.handleEvent);
                document.addEventListener('touchmove', this.handleEvent);
                document.addEventListener('mouseup', this.handleEvent);
                document.addEventListener('touchend', this.handleEvent);
                document.addEventListener('touchcancel', this.handleEvent);

                // Set sliderLeft position on load
                this.sliderHandleLeft.style.left = this.getPosition(
                    parseInt(this.sliderHandleLeft.getAttribute('aria-valuemax'), 0),
                    parseInt(this.sliderHandleLeft.getAttribute('aria-valuemin'), 0),
                    parseInt(this.sliderHandleLeft.getAttribute('aria-valuenow'), 0)
                );

                // Set sliderRight position on load
                this.sliderHandleRight.style.left = this.getPosition(
                    parseInt(this.sliderHandleRight.getAttribute('aria-valuemax'), 0),
                    parseInt(this.sliderHandleRight.getAttribute('aria-valuemin'), 0),
                    parseInt(this.sliderHandleRight.getAttribute('aria-valuenow'), 0)
                );
            }
        }
    }

    /**
     * Responsible for clearing the document.body scroll lock in the event of a dodgy 
     * touchup misfire. This could occur if a user gets a phone call while dragging, etc.
     */
    removeBodyLock() {
        const activeHandleCheckInterval = setInterval(() => {
            if (this.activeHandle == null) {
                document.body.classList.remove('body-lock');
                clearInterval(activeHandleCheckInterval);
            }
        }, 200);
    }

    getPosition (max, min, value)
    {
        // Grab the slider and calculate the value as well as the position
        const sliderRect = this.slider.getBoundingClientRect();
        const sliderWidth = sliderRect.width;
        const position = (value - min) / (max - min) * sliderWidth;

        return position + 'px';
    }

    /**
     * Processes the events that are fired by the slider.
     * 
     * @param {*} event The event to process
     * @returns null
     */
    handleEvent (event)
    {
        if (event.type === 'mousedown' || event.type === 'touchstart') 
        {
            // Set the active handle when the user clicks/touches
            this.activeHandle = event.target;
        } else if (event.type === 'mousemove' || event.type === 'touchmove') 
        {
            if (!this.activeHandle)
                return;

            // Grab the slider and calculate the value as well as the position
            const sliderRect = this.slider.getBoundingClientRect();
            const sliderWidth = sliderRect.width;

            const min = parseInt(this.activeHandle.getAttribute('aria-valuemin'), 0);
            const max = parseInt(this.activeHandle.getAttribute('aria-valuemax'), 10);

            // Get the ratio and value relative to the document width
            let ratio;
            if (event.type === 'mousemove') {
                ratio = (event.clientX - sliderRect.left) / sliderWidth;
            } else if (event.type === 'touchmove') {
                ratio = (event.touches[0].clientX - sliderRect.left) / sliderWidth;

                document.body.classList.add('body-lock');
                this.removeBodyLock();
            }
            ratio = Math.max(0, Math.min(1, ratio)); // Clamp ratio between 0 and 1
            const value = Math.round(ratio * (max - min) + min);

            // Complete checks to ensure that the slider handles don't overlap, etc.
            if (this.activeHandle === this.sliderHandleLeft && value > parseInt(this.sliderHandleRight.getAttribute('aria-valuenow'), 10))
                return;

            if (this.activeHandle === this.sliderHandleRight && value < parseInt(this.sliderHandleLeft.getAttribute('aria-valuenow'), 10))
                return;

            if (this.activeHandle && typeof this.activeHandle !== null && !this.activeHandle.classList.contains('slider-handle-active'))
                this.activeHandle.classList.add('slider-handle-active');

            this.activeHandle.setAttribute('aria-valuenow', value);

            // Calculate and set the position
            const position = (value - min) / (max - min) * sliderWidth;
            this.activeHandle.style.left = position + 'px';
            this.activeHandle.children[0].innerHTML = value;
        } else if (event.type === 'mouseup' || event.type === 'touchend' || event.type === 'touchcancel') 
        {
            document.body.classList.remove('body-lock');

            if (this.activeHandle && typeof this.activeHandle !== null)
                this.activeHandle.classList.remove('slider-handle-active');

            this.activeHandle = null;

            // Check if the user ended the click somewhere on <price-slider>
            if (this.contains(event.target))
            {
                // Set the current value and fire the inputchange event to trigger a reload
                let minInput = document.querySelector(`.${FacetFiltersForm.filterRange}-min`);
                let maxInput = document.querySelector(`.${FacetFiltersForm.filterRange}-max`);

                if (minInput && typeof minInput !== 'undefined' && maxInput && typeof maxInput !== 'undefined')
                {
                    minInput.value = this.sliderHandleLeft.getAttribute('aria-valuenow');
                    maxInput.value = this.sliderHandleRight.getAttribute('aria-valuenow');

                    const event = new Event('input', { bubbles: true });
                    minInput.dispatchEvent(event);
                }
            }
        } else if (event.type == 'mouseover' || event.type == 'mouseleave')
        {
            if (event.target && typeof event.target !== 'undefined') {
                if (event.type == 'mouseover')
                    event.target.classList.add('slider-handle-active');

                if (event.type == 'mouseleave')
                    event.target.classList.remove('slider-handle-active');
            }
        }
    }
}

customElements.define('price-slider', PriceSlider);

class ActiveFilter extends HTMLElement 
{
  constructor () {
    super();

    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.remove.bind(this));
  }

  remove (event) 
  {
    event.preventDefault();

    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('active-filter', ActiveFilter);

// --- AVAILABILITY TOGGLE LOGIC START ---
const AVAILABILITY_FILTER_KEY = 'filter.p.m.filter.product_availability';
const AVAILABILITY_FILTER_VALUES = ['In Stock']; // ['Back Order', 'In Stock'];

function getAvailabilityFromParams(params) {
    let values = [];
    for (const [key, value] of params.entries()) {
        if (key === AVAILABILITY_FILTER_KEY) values.push(value.replace(/\+/g, ' '));
    }
    return values;
}

function isAvailabilityFilterActive() {
    const params = new URLSearchParams(window.location.search);
    const values = getAvailabilityFromParams(params);
    return AVAILABILITY_FILTER_VALUES.every(v => values.includes(v));
}

function buildSearchParamsWithAvailability(form, checked) {
    const formData = new FormData(form);
    let params = [];
    for (const [key, value] of formData.entries()) {
        if (key !== AVAILABILITY_FILTER_KEY) {
            params.push([key, value]);
        }
    }
    if (checked) {
        AVAILABILITY_FILTER_VALUES.forEach(val => {
            params.push([AVAILABILITY_FILTER_KEY, val]);
        });
    }
    return params.map(([k, v]) => encodeURIComponent(k) + '=' + encodeURIComponent(v)).join('&');
}

// Attach or re-attach the toggle logic
function setupInstockToggle() {
    setTimeout(() => {
        const instockToggle = document.querySelector('.show-instock-only input[type="checkbox"]');
        if (!instockToggle) return;

        // Remove any previous event listener by using a named handler and removing it first
        if (instockToggle._instockHandler) {
            instockToggle.removeEventListener('change', instockToggle._instockHandler);
        }

        // Always set checked state based on current URL
        instockToggle.checked = isAvailabilityFilterActive();

        // Define the handler and store it on the element for future removal
        instockToggle._instockHandler = function (e) {
            e.stopPropagation();
            // Always re-query the toggle in case of DOM replacement
            const toggle = document.querySelector('.show-instock-only input[type="checkbox"]');
            const form = document.getElementById('FiltersForm');
            if (!form || !toggle) return;
            const searchParams = buildSearchParamsWithAvailability(form, toggle.checked);
            FacetFiltersForm.renderPage(searchParams, { srcElement: toggle });
        };
        instockToggle.addEventListener('change', instockToggle._instockHandler);

        // Keep toggle in sync with browser navigation
        if (!window._instockPopstateHandler) {
            window._instockPopstateHandler = function () {
                const toggle = document.querySelector('.show-instock-only input[type="checkbox"]');
                if (toggle) toggle.checked = isAvailabilityFilterActive();
            };
            window.addEventListener('popstate', window._instockPopstateHandler);
        }
    }, 0); // Ensure DOM is ready
}
// --- AVAILABILITY TOGGLE LOGIC END ---

document.addEventListener('DOMContentLoaded', function () {
    setupInstockToggle();
});

// Patch FacetFiltersForm.renderData to re-setup the toggle after AJAX
const origRenderData = FacetFiltersForm.renderData;
FacetFiltersForm.renderData = function(html, event) {
    origRenderData.call(this, html, event);
    setupInstockToggle();
};